/**
 * "Esqueceu o treino aberto?" (`D245`) — when to ask, and the ends to offer.
 * PURE: `now` and the person's recent durations are parameters.
 *
 * ── WHEN ───────────────────────────────────────────────────────────────────
 * On a launch and on every return to the front, a running workout is asked
 * about when BOTH hold:
 *
 * - it has been open at least the person's threshold
 *   ({@link forgottenThresholdMs}): twice their typical workout, never under
 *   {@link FORGOTTEN_MIN_MS} and never over {@link FORGOTTEN_MAX_MS} — three
 *   hours, half of the server's six-hour auto-close (`D177`), so the
 *   question always comes first; and
 * - nothing has happened for {@link FORGOTTEN_IDLE_MS}: no set, no pause or
 *   resume. A long workout still being logged is not a forgotten one.
 *
 * "Continuar treinando" holds the question off for one more threshold.
 *
 * ── THE ENDS ───────────────────────────────────────────────────────────────
 * At the last set, half an hour after it, or now — each only when it is in
 * the past and distinct. With no set logged there is no end worth recording,
 * so only "keep going" and "discard" are offered.
 */
import type { WorkoutSession } from "../store/session-model";

const MINUTE_MS = 60 * 1000;
const HOUR_MS = 60 * MINUTE_MS;

/** Without enough history, and the most the threshold ever is. */
export const FORGOTTEN_MAX_MS = 3 * HOUR_MS;
/** The least the threshold ever is, however short the person's workouts. */
export const FORGOTTEN_MIN_MS = 90 * MINUTE_MS;
/** How long the workout must have been left alone. */
export const FORGOTTEN_IDLE_MS = 30 * MINUTE_MS;
/** "Meia hora depois". */
export const FORGOTTEN_LATER_MS = 30 * MINUTE_MS;
/** Durations needed before the person's own typical one is trusted. */
export const FORGOTTEN_MIN_SAMPLES = 3;

export type ForgottenSession = Pick<
  WorkoutSession,
  "ended" | "keptOpenAt" | "logged" | "pauses" | "startedAt"
>;

/** Twice the median of `durationsMs`, clamped; three hours without enough. */
export function forgottenThresholdMs(durationsMs: readonly number[]): number {
  const usable = durationsMs.filter((ms) => ms > 0).sort((a, b) => a - b);
  if (usable.length < FORGOTTEN_MIN_SAMPLES) {
    return FORGOTTEN_MAX_MS;
  }
  const middle = Math.floor(usable.length / 2);
  const median =
    usable.length % 2 === 1
      ? (usable[middle] ?? 0)
      : ((usable[middle - 1] ?? 0) + (usable[middle] ?? 0)) / 2;
  return Math.min(FORGOTTEN_MAX_MS, Math.max(FORGOTTEN_MIN_MS, 2 * median));
}

/** The recent workouts' spans: ended, and not abandoned (`D177`). */
export function typicalDurationsMs(
  sessions: readonly {
    readonly abandoned?: true | undefined;
    readonly endedAt: string | null;
    readonly startedAt: string;
  }[]
): number[] {
  const out: number[] = [];
  for (const session of sessions) {
    if (session.endedAt === null || session.abandoned === true) {
      continue;
    }
    const span = Date.parse(session.endedAt) - Date.parse(session.startedAt);
    if (Number.isFinite(span) && span > 0) {
      out.push(span);
    }
  }
  return out;
}

/** The last set's moment, or `null` with nothing logged. */
export function lastSetAt(
  session: Pick<WorkoutSession, "logged">
): number | null {
  let last: number | null = null;
  for (const sets of Object.values(session.logged)) {
    for (const set of sets) {
      if (last === null || set.performedAt > last) {
        last = set.performedAt;
      }
    }
  }
  return last;
}

/** The last thing the person did with this workout. */
export function lastActivityAt(session: ForgottenSession): number {
  let at = Math.max(session.startedAt, lastSetAt(session) ?? 0);
  for (const pause of session.pauses ?? []) {
    at = Math.max(at, pause.from, pause.to ?? 0);
  }
  return Math.max(at, session.keptOpenAt ?? 0);
}

export type EndChoiceKind = "last" | "later" | "now";

export interface EndChoice {
  /** Epoch ms the workout is ended at. */
  readonly at: number;
  readonly kind: EndChoiceKind;
}

export interface ForgottenPrompt {
  /** The ends to offer, earliest first; empty with nothing logged. */
  readonly choices: readonly EndChoice[];
  readonly lastSetAt: number | null;
  /** How long the workout has been open, ms. */
  readonly openMs: number;
}

/** Whether to ask now, and what; `null` = leave the workout alone. */
export function forgottenPrompt(
  session: ForgottenSession,
  now: number,
  thresholdMs: number
): ForgottenPrompt | null {
  if (session.ended !== null) {
    return null;
  }
  const openMs = now - session.startedAt;
  if (openMs < thresholdMs) {
    return null;
  }
  if (now - lastActivityAt(session) < FORGOTTEN_IDLE_MS) {
    return null;
  }
  if (
    session.keptOpenAt !== undefined &&
    now - session.keptOpenAt < thresholdMs
  ) {
    return null;
  }
  const last = lastSetAt(session);
  const choices: EndChoice[] = [];
  if (last !== null) {
    choices.push({ at: last, kind: "last" });
    const later = last + FORGOTTEN_LATER_MS;
    if (later < now - MINUTE_MS) {
      choices.push({ at: later, kind: "later" });
    }
    choices.push({ at: now, kind: "now" });
  }
  return { choices, lastSetAt: last, openMs };
}
